/**
 * Match outcome semantics
 * Pure, side-effect-free rules for the winner-less outcomes (dual_withdrawal,
 * not_played) alongside the existing played/walkover outcomes. `retired` is a played match
 * cut short by a retirement: it keeps its score and always has a winner (the opponent).
 */

export type MatchOutcome = 'played' | 'walkover' | 'dual_withdrawal' | 'not_played' | 'retired';

/** Counting semantics per outcome. `satisfies` makes a 5th outcome a compile error here
 *  and at every exhaustive switch — extend THIS table first, then follow the errors. */
export const OUTCOME_RULES = {
  played: { countsAsPlayed: true, creditsLoss: 'winner_loser' },
  walkover: { countsAsPlayed: true, creditsLoss: 'winner_loser' },
  dual_withdrawal: { countsAsPlayed: true, creditsLoss: 'both' },
  not_played: { countsAsPlayed: false, creditsLoss: 'none' },
  retired: { countsAsPlayed: true, creditsLoss: 'winner_loser' },
} satisfies Record<
  MatchOutcome,
  { countsAsPlayed: boolean; creditsLoss: 'winner_loser' | 'both' | 'none' }
>;

const WINNERLESS: ReadonlySet<string> = new Set(['walkover', 'dual_withdrawal', 'not_played']);

/** True when the outcome means "no score was ever produced". Accepts raw DB strings. */
export const isWinnerless = (o: MatchOutcome | string | null | undefined): boolean =>
  o != null && WINNERLESS.has(o);

/** Row-level predicate with the legacy fallback: outcome authoritative when set,
 *  else fall back to is_walkover (pre-column rows). The replacement for `!is_walkover` guards. */
export const isWinnerlessRow = (row: {
  outcome?: string | null;
  is_walkover?: boolean | null;
}): boolean => (row.outcome != null ? isWinnerless(row.outcome) : row.is_walkover === true);
